import React, { useState } from "react";
import { X, CheckCircle, Plus, Trash2, Database, Palette, Settings } from "lucide-react";
import { DataContentModal } from "./DataContentModal";

interface ConfigureInformationLayersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface InformationLayerItem {
  id: string;
  name: string;
  selectedMetric: string;
  baseColor: string;
}

export const ConfigureInformationLayersModal: React.FC<ConfigureInformationLayersModalProps> = ({
  isOpen,
  onClose
}) => {
  const [layers, setLayers] = useState<InformationLayerItem[]>([
    { id: "1", name: "Standard Plan", selectedMetric: "", baseColor: "" },
    { id: "2", name: "Sales Productivity PSF", selectedMetric: "", baseColor: "" },
    { id: "3", name: "Occupancy Cost", selectedMetric: "", baseColor: "" },
    { id: "4", name: "Total Rent PSF", selectedMetric: "", baseColor: "" },
    { id: "5", name: "Base Plan", selectedMetric: "", baseColor: "" },
  ]);

  const [activeDataContentId, setActiveDataContentId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleUpdateLayer = (id: string, updates: Partial<InformationLayerItem>) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const handleAddLayer = () => {
    setLayers(prev => [
      ...prev,
      { id: Date.now().toString(), name: "New Layer", selectedMetric: "", baseColor: "" }
    ]);
  };

  const handleRemoveLayer = (id: string) => {
    setLayers(prev => prev.filter(l => l.id !== id));
  };

  return (
    <>
      <div className="flex flex-col flex-1 overflow-hidden bg-white">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100">
              <Settings className="h-4 w-4 text-indigo-600" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 leading-tight">Information Layers Setup</h2>
              <p className="text-[10px] text-gray-500 leading-tight">Set business metrics for generating views.</p>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 flex-1 overflow-y-auto bg-white">
          <div className="w-full">
            <div className="space-y-2">
              {layers.map((layer, index) => (
                <div 
                  key={layer.id}
                  className="flex flex-col gap-2 bg-slate-50 p-2.5 rounded-md border border-slate-200 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-1">
                      <div className="text-slate-400 font-medium text-xs w-3 text-right">
                        {index + 1}.
                      </div>
                      <input
                        type="text"
                        value={layer.name}
                        onChange={(e) => handleUpdateLayer(layer.id, { name: e.target.value })}
                        className="flex-1 bg-white border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-1.5 py-1 text-xs font-medium text-slate-700 transition-colors"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveLayer(layer.id)}
                      className="text-slate-400 hover:text-red-500 p-0.5 rounded transition-colors"
                      title="Remove layer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pl-4">
                    <div className="flex-1 flex items-center gap-1.5">
                      <button
                        onClick={() => setActiveDataContentId(layer.id)}
                        className="flex items-center justify-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded-md text-indigo-700 hover:bg-indigo-100 bg-indigo-50 border border-indigo-200 transition-colors whitespace-nowrap"
                      >
                        <Database size={12} />
                        {layer.selectedMetric ? "Edit Data" : "Data Content"}
                      </button>
                      {layer.selectedMetric && (
                        <div className="flex-1 min-w-0 bg-white border border-indigo-100 rounded px-1.5 py-1 flex items-center shadow-sm">
                          <span className="text-[10px] font-semibold text-indigo-700 truncate" title={layer.selectedMetric}>
                            {layer.selectedMetric}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      className="flex items-center justify-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded-md text-emerald-700 hover:bg-emerald-100 bg-emerald-50 border border-emerald-200 transition-colors whitespace-nowrap sm:w-auto w-full"
                      title="Future enhancement: Assign base color"
                    >
                      <Palette size={12} />
                      Base Color
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={handleAddLayer}
                className="w-full py-2 mt-2 border-2 border-dashed border-slate-300 rounded-md text-slate-500 font-medium text-[11px] flex items-center justify-center gap-1.5 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
              >
                <Plus size={14} />
                Add Layer
              </button>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  alert("Information Layers saved! Server generation process will be connected here.");
                  onClose();
                }}
                className="flex items-center justify-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded shadow-sm transition-colors w-full"
              >
                <CheckCircle size={14} />
                Save Layers
              </button>
            </div>
          </div>
        </div>
      </div>

      <DataContentModal
        isOpen={activeDataContentId !== null}
        onClose={() => setActiveDataContentId(null)}
        initialMetric={activeDataContentId ? layers.find(l => l.id === activeDataContentId)?.selectedMetric || "" : ""}
        onSave={(metric) => {
          if (activeDataContentId) {
            handleUpdateLayer(activeDataContentId, { selectedMetric: metric });
          }
        }}
      />
    </>
  );
};
